import type { VinylAmznTrackLoadOptions } from '@amzn/vinyl-amzn'
import type { PlayableEntity } from '@/service/fragments/playableEntityFrag'
import type { ImageFields } from '@/service/fragments/imageFieldsFrag'
import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import {
    type Clearable,
    createDisposer,
    type Disposable,
    logDebug,
    type Maybe,
    type ValueProvider,
} from '@amazon/vinyl-util'
import type { PlayParams } from '@/service/fragments/playParamsFrag'
import type { CreateCloudQueueOptions } from '@/cloudQueue/cloudQueueService'
import { notSignedIn } from '@/auth/authRef'
import type { FeedProvider } from '@/util/async/FeedProvider'
import { ctx } from '@/util/context'

const { target } = ctx('PlaylistController')

export type PlaylistEntry = {
    readonly id: string
    readonly entity: PlayableEntity
    readonly title: string
    readonly albumTitle: string
    readonly artistName: string
    /**
     * Optional ids the transport uses to link the title / album /
     * artist labels to their detail pages. Tracks resolve all three
     * (artistId from the first contributing artist); podcasts and
     * videos leave them off when there's no obvious target.
     */
    readonly trackId?: string | null
    readonly albumId?: string | null
    readonly artistId?: string | null
    readonly duration: number
    readonly image: ImageFields | null
    /**
     * Track load options used by the player. Either supplied
     * directly, or as a thunk that resolves them lazily — useful for
     * video entries where PRS resolution costs a network round trip
     * and we want the UI (artwork, title, transport position) to
     * update before the manifest URL lands.
     */
    readonly trackLoadOptions: ValueProvider<VinylAmznTrackLoadOptions>
    /**
     * True when the entry should render the video stage. Authoritative
     * — driven by the queue source rather than by sniffing media
     * dimensions, which is racy during load. Audio entries leave this
     * undefined / false.
     */
    readonly video?: boolean
    /**
     * CloudQueue's per-instance reference id for this entry, when the
     * feed is backed by CloudQueue. Needed to anchor `queue(view)` to
     * the right item — without it the gateway returns no edges. Local
     * (non-cloud-queue) feeds leave this undefined.
     */
    readonly entityReferenceId?: string | null
}

/**
 * Capability flags returned by the cloud queue gateway alongside the
 * queue. Mirrors hornet's `QueueCapability` shape — the gateway
 * rejects setQueueShuffleMode / setQueueLoopMode for queues whose
 * `shuffle.isAllowed` / `loopAll.isAllowed` is false, so the
 * transport must consult these to enable / disable buttons rather
 * than always offering them.
 */
export interface QueueCapabilities {
    readonly shuffleAllowed: boolean
    readonly loopAllAllowed: boolean
    readonly blockQueueMutations: boolean
}

/**
 * Pure-functional feed of PlaylistEntry. The controller asks for the
 * next/previous entry relative to whichever entry it's currently on;
 * the feed handles cursor bookkeeping internally.
 */
export interface PlaylistDataProvider extends FeedProvider<PlaylistEntry> {
    /**
     * The parameters used to create the CloudQueue, if the playlist is
     * using CloudQueue.
     */
    readonly cloudQueueOptions?: Maybe<CreateCloudQueueOptions>

    /**
     * Capabilities the cloud queue gateway returned for this queue.
     * Absent for non-cloud feeds; transport defaults to "all
     * allowed" in that case.
     */
    readonly queueCapabilities?: Maybe<QueueCapabilities>
}

export interface PlaylistController extends Clearable {
    /** The currently-selected entry, or null if nothing is queued. */
    readonly current: ObservableValue<PlaylistEntry | null>

    /**
     * The entry that will play after `current`. Refreshed automatically
     * whenever `current` changes (the controller asks the feed for
     * `next(current)` and writes the result here once it resolves).
     * Holds null while the lookup is in flight, when the feed has no
     * further items, or when no feed is set. Use `invalidateNext` to
     * force a re-fetch — useful when the feed's contents shift out
     * from under us (e.g. a guest adds a track to a party queue).
     */
    readonly next: ObservableValue<PlaylistEntry | null>

    /** The CloudQueue options from the active feed, if any. */
    readonly cloudQueueOptions: ObservableValue<Maybe<CreateCloudQueueOptions>>

    /**
     * Capabilities reported by the active cloud queue. Null when no
     * feed is set or the feed didn't surface capabilities. Transport
     * surfaces consult `shuffleAllowed` / `loopAllAllowed` so the
     * shuffle / repeat buttons disable themselves on queues where
     * those mutations would 400 — gateway returns
     * "Shuffle not supported on this queue" otherwise.
     */
    readonly queueCapabilities: ObservableValue<Maybe<QueueCapabilities>>

    /** PlayParams projection of cloudQueueOptions, for convenience. */
    readonly playParams: ObservableValue<Maybe<PlayParams>>

    /**
     * Advance to the next entry. Updates `current` and returns the new
     * value. Resolves to null when the feed has nothing further; the
     * controller leaves current pointing at the last entry in that
     * case so transports can show the final track.
     */
    skipNext(): Promise<PlaylistEntry | null>

    /**
     * Step back to the previous entry. Same return semantics as
     * skipNext.
     */
    skipPrevious(): Promise<PlaylistEntry | null>

    /**
     * Refresh the `next` observable. Drops whatever lookup is cached
     * and asks the feed again from the current entry. Useful when
     * the feed's contents may have shifted (e.g. a guest added a
     * track to a party queue and the previously-resolved next is no
     * longer authoritative — or there was no next at all and now
     * there is one).
     */
    invalidateNext(): void

    /**
     * Monotonic counter that increments each time the underlying
     * queue's edges change due to a local mutation (addToQueue,
     * insertIntoQueue, removeFromQueue called via the controller).
     * Surfaces driven by the controller — the now-playing queue rail,
     * for instance — subscribe to this to know when to re-fetch their
     * own view of the queue. Distinct from `invalidateNext`, which
     * only nudges the next-prefetch.
     */
    readonly queueMutatedAt: ObservableValue<number>

    /**
     * Bump `queueMutatedAt`. Call after a successful queue-mutation
     * RPC (addToQueue, insertIntoQueue, removeFromQueue) so views
     * subscribed to the controller can refresh.
     */
    notifyQueueMutated(): void

    /**
     * Seek directly onto a specific entry inside the existing feed.
     * Used by surfaces that let the user pick a track from the queue
     * (e.g. the now-playing queue rail) — we want to start playing
     * the chosen track without re-creating the cloud queue, which
     * would lose the parent container context (album, playlist, …).
     *
     * The caller is responsible for ensuring the entry is one the
     * feed knows about (via its provider-specific cursor) — for the
     * cloud-queue feed that means seeding entityReferenceId via
     * registerEntryRef before calling.
     */
    seekToEntry(entry: PlaylistEntry): void

    /**
     * Local-only repeat mode. The cloud queue can carry its own
     * `loopMode` server-side (set at queue creation), but
     * mid-playback toggling routes through the controller — the
     * binding consults this when the player fires `ended` to decide
     * whether to seek-and-replay vs auto-advance.
     *
     * Cycles off → all → one with a single button:
     *   - off: fall through to skipNext on `ended`.
     *   - all: same as off; the cloud queue's own loopMode/feed
     *     terminator decides.
     *   - one: seek to 0 + play instead of advancing.
     */
    readonly repeatMode: MutableValue<RepeatMode>

    /**
     * Local shuffle toggle. When on, `skipNext()` from the transport
     * is rerouted through `skipNextRandom()` so playback jumps to a
     * random upcoming entry instead of the linear next.
     */
    readonly shuffleEnabled: MutableValue<boolean>
}

export type RepeatMode = 'off' | 'all' | 'one'

/**
 * A PlaylistController whose backing feed can be (re)set.
 */
export interface ManagedPlaylistController extends PlaylistController {
    setDataProvider(value: PlaylistDataProvider): void
}

export class PlaylistControllerImpl implements ManagedPlaylistController, Disposable {
    get [Symbol.toStringTag]() {
        return 'PlaylistController'
    }

    private readonly _cloudQueueOptions = data<Maybe<CreateCloudQueueOptions>>(null)
    public readonly playParams: ObservableValue<Maybe<PlayParams>> =
        this._cloudQueueOptions.pick('playParams')

    public get cloudQueueOptions(): ObservableValue<Maybe<CreateCloudQueueOptions>> {
        return this._cloudQueueOptions
    }

    private readonly _queueCapabilities = data<Maybe<QueueCapabilities>>(null)
    public get queueCapabilities(): ObservableValue<Maybe<QueueCapabilities>> {
        return this._queueCapabilities
    }

    private readonly _current = data<PlaylistEntry | null>(null)
    public get current(): ObservableValue<PlaylistEntry | null> {
        return this._current
    }

    private readonly _next = data<PlaylistEntry | null>(null)
    public get next(): ObservableValue<PlaylistEntry | null> {
        return this._next
    }

    private readonly _queueMutatedAt = data(0)
    public get queueMutatedAt(): ObservableValue<number> {
        return this._queueMutatedAt
    }

    /**
     * Local play-mode flags. Both default off; the player binding
     * consults `repeatMode` on `ended`, and the transport's Next
     * button consults `shuffleEnabled` to decide between linear and
     * random advance. Persistence isn't wired — these reset to
     * defaults on a hard reload.
     */
    public readonly repeatMode: MutableValue<RepeatMode> = data<RepeatMode>('off')
    public readonly shuffleEnabled: MutableValue<boolean> = data(false)

    notifyQueueMutated(): void {
        this._queueMutatedAt.value = this._queueMutatedAt.value + 1
        this.refreshNext()
    }

    private feed: PlaylistDataProvider | null = null

    /**
     * Set to true when a `skipNext()` call resolves with no next
     * entry — i.e. we've fallen off the tail of the feed. Cleared
     * any time we successfully advance/rewind/jump. While true, a
     * subsequent `refreshNext()` that resolves to a non-null entry
     * (e.g. a guest just added a track to the party queue) auto-
     * advances onto it instead of leaving the user marooned at the
     * end. See `refreshNext` for the auto-advance branch.
     */
    private atTail = false

    /**
     * Token for the in-flight `next` lookup. Bumped on every refresh
     * so older fetches can detect that they've been superseded and
     * not stomp `_next` with a stale value.
     */
    private nextToken = 0

    /**
     * Promise of the in-flight `next` lookup. `skipNext()` awaits this
     * to avoid issuing a duplicate feed.next() when the binding has
     * already kicked one off — the common case is "current changes →
     * preload-prefetch fires → user immediately hits the skip
     * button".
     */
    private nextPromise: Promise<PlaylistEntry | null> = Promise.resolve(null)

    private readonly disposer = createDisposer()

    constructor() {
        const { add } = this.disposer
        add(
            notSignedIn().onData((value) => {
                if (value) this.clear()
            })
        )
        // Whenever the current entry changes, kick off a fresh next
        // lookup so the `next` observable converges automatically.
        // First emission fires with current=null (no feed) → null
        // is the correct value.
        add(
            this._current.onData((entry) => {
                logDebug(target, 'current changed:', {
                    id: entry?.id ?? null,
                    title: entry?.title ?? null,
                    video: entry?.video === true,
                })
            })
        )
        // Toggling shuffle / repeat changes which entry comes after
        // the current one — the cached `next` lookup is stale at
        // that point. Drop it so the next button / preload binding
        // re-resolves with the new mode in mind. We skip the very
        // first emission (initial defaults) so we don't fire a
        // pointless invalidate before any feed is set.
        let firstShuffle = true
        add(
            this.shuffleEnabled.onData(() => {
                if (firstShuffle) {
                    firstShuffle = false
                    return
                }
                this.invalidateNext()
            })
        )
        let firstRepeat = true
        add(
            this.repeatMode.onData(() => {
                if (firstRepeat) {
                    firstRepeat = false
                    return
                }
                this.invalidateNext()
            })
        )
    }

    setDataProvider(provider: PlaylistDataProvider): void {
        this.clear()
        this.feed = provider
        this._cloudQueueOptions.value = provider.cloudQueueOptions ?? null
        this._queueCapabilities.value = provider.queueCapabilities ?? null
        this.nextPromise = this.feed.next(null)
    }

    clear(): void {
        this.feed = null
        this.nextToken++
        this._next.value = null
        this._current.value = null
        this._cloudQueueOptions.value = null
        this._queueCapabilities.value = null
        this.atTail = false
    }

    async skipNext(): Promise<PlaylistEntry | null> {
        if (!this.feed) return null
        // Use the prefetched next promise.
        let next = await this.nextPromise
        if (next === null && this._current.value) {
            // If the prefetched next promise resolves to null, try again with the feed provider.
            next = await this.feed.next(this._current.value)
        }
        if (next != null) {
            this.atTail = false
            this._current.value = next
            this.refreshNext()
        } else {
            // No next available — remember we've fallen off the
            // tail so a later invalidateNext() (e.g. a guest adds a
            // track to the party queue) can auto-advance onto the
            // new entry instead of leaving us stuck on the last
            // track. See refreshNext.
            this.atTail = true
        }
        return next
    }

    async skipPrevious(): Promise<PlaylistEntry | null> {
        if (!this.feed || !this._current.value) return null
        const prev = await this.feed.previous(this._current.value)
        if (prev != null) {
            this.atTail = false
            this._current.value = prev
            this.refreshNext()
        }
        return prev
    }

    invalidateNext(): void {
        this.refreshNext()
    }

    seekToEntry(entry: PlaylistEntry): void {
        if (!this.feed) return
        // Hop directly onto the entry. playerPlaylistBinding is
        // wired to current.onData so it'll load + play; we kick the
        // next lookup so transports show the correct disabled state.
        this.atTail = false
        this._current.value = entry
        this.refreshNext()
    }

    private refreshNext(): void {
        const my = ++this.nextToken
        const fromEntry = this._current.value
        if (!fromEntry || !this.feed) {
            this.nextPromise = Promise.resolve(null)
            this._next.value = null
            return
        }
        const promise = this.feed.next(fromEntry)
        this.nextPromise = promise
        promise
            .then((next) => {
                // Only the most recent lookup wins — earlier ones can
                // race in if the user is rapidly skipping or the feed
                // is invalidated mid-flight.
                if (my !== this.nextToken) return
                this._next.value = next
                // We previously fell off the tail (skipNext returned
                // null after the last track ended) and now the feed
                // has a fresh entry — auto-advance onto it. The
                // common trigger is a guest adding a track to the
                // party queue while playback is parked at the end.
                if (next != null && this.atTail) {
                    this.atTail = false
                    this._current.value = next
                    this.refreshNext()
                }
            })
            .catch(() => {
                if (my !== this.nextToken) return
                this._next.value = null
            })
    }

    dispose() {
        this.clear()
        this.disposer.dispose()
    }
}

/**
 * Returns true if what the given playlistController is currently playing either matches the given playParams id or
 * one of its containers.
 *
 * @param playlistController
 * @param playParams
 */
export function isCurrentPlaylistOrContainer(
    playlistController: PlaylistController,
    playParams: Maybe<PlayParams>
): boolean {
    const id = playParams?.id
    if (!id) return false
    const currentlyPlayingParams = playlistController.playParams.value
    // Check if the playlist controller is set to a matching container.
    if (currentlyPlayingParams?.id === id) {
        return true
    }
    if (!currentlyPlayingParams?.containerIds?.length) return false
    return currentlyPlayingParams.containerIds.includes(id)
}
